import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, ChevronRight, Clock, ThumbsUp, Share2, CheckCircle2, FileText, Image as ImageIcon, Settings, Sparkles } from "lucide-react";

export default function DocumentosTutorialPage() {
  const { user } = useAuth();
  
  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 font-sans pb-20">
      <header className="border-b bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/">
            <img src="/marketing/logo_official.png" className="h-28 object-contain" alt="OdontoAssist AI Logo" />
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/ajuda/tutoriais" className="text-gray-500 hover:text-indigo-600 font-semibold hidden md:block">
              Central de Guias
            </Link>
            <Link 
              to="/" 
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl font-black tracking-wide flex items-center gap-2 transition-all shadow-lg hover:-translate-y-1"
            >
              <ArrowLeft size={20} />
              Voltar {user ? "para o Aplicativo" : "ao Início"}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 mb-6">
          <Link to="/ajuda" className="hover:text-indigo-600">Ajuda</Link>
          <ChevronRight size={14} className="text-gray-400" />
          <Link to="/ajuda/tutoriais" className="hover:text-indigo-600">Guias</Link>
          <ChevronRight size={14} className="text-gray-400" />
          <span className="text-indigo-600">Gestão Clínica</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight">
          Gerando Receituários e Contratos
        </h1>
        
        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-8">
          <div className="flex items-center gap-2">
            <Clock size={16} /> <span>9 min de conteúdo dividido em 3 vídeos</span>
          </div>
          <div className="flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-bold uppercase tracking-wider text-xs">
            Gestão Clínica
          </div>
        </div>

        <div className="space-y-10 mb-12">

          {/* Vídeo 1 */}
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-4">
              1. Configurando a Identidade Visual
            </h2>

            <div className="w-full aspect-video rounded-3xl overflow-hidden relative shadow-2xl border-4 border-white bg-black">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/DDvxpb3TCnM"
                title="Configurando a Identidade Visual"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full"
              ></iframe>
            </div>
          </div>


          {/* Vídeo 2 */}
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-4">
              2. Templates e Documentos Clínicos
            </h2>

            <div className="w-full aspect-video rounded-3xl overflow-hidden relative shadow-2xl border-4 border-white bg-black">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/mVr92fN42zM"
                title="Templates e Documentos Clínicos"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full"
              ></iframe>
            </div>
          </div>


          {/* Vídeo 3 */}
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-4">
              3. Contratos Digitais e Validação
            </h2>

            <div className="w-full aspect-video rounded-3xl overflow-hidden relative shadow-2xl border-4 border-white bg-black">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/zNhJydD0wKo"
                title="Contratos Digitais e Validação"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full"
              ></iframe>
            </div>
          </div>

        </div>

        <div className="flex items-center justify-between border-b border-gray-200 pb-8 mb-10">
          <div className="flex items-center gap-4">
            <img src="https://i.pravatar.cc/150?img=47" alt="Instrutor" className="w-12 h-12 rounded-full border-2 border-white shadow-sm" />
            <div>
              <p className="font-bold text-gray-900">Equipe de Suporte</p>
              <p className="text-sm text-gray-500">Desenvolvimento de Produto</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition-colors">
              <ThumbsUp size={18} /> Útil
            </button>
            <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition-colors">
              <Share2 size={18} /> Compartilhar
            </button>
          </div>
        </div>

        {/* Written Tutorial */}
        <article className="prose prose-indigo prose-lg max-w-none text-gray-600 text-justify">
          <p className="lead text-xl text-gray-700 font-medium">
            Receituários, atestados, declarações e contratos fazem parte da rotina clínica. 
            Com o OdontoAssist AI, você configura sua identidade visual uma única vez e gera documentos profissionais, seguros e padronizados em poucos cliques.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Passo a Passo em Texto</h2>
          
          <div className="space-y-8 mt-8">
            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">1</span>
                 Configurando o Visual (Papel Timbrado)
               </h3>
               <p>
                 Antes de emitir qualquer documento, configure a identidade visual da sua clínica.
               </p>

               <p>
                 O papel timbrado configurado aqui poderá ser utilizado em receituários, atestados, declarações e contratos.
               </p>
               
               <p>Acesse o painel lateral em <strong>Documentos Clínicos </strong>ou <strong>Contratos &gt; Visual</strong>. Você possui duas opções muito poderosas para editar:</p>
               
               <div className="grid md:grid-cols-2 gap-4 mt-4">
                 <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <h4 className="font-bold flex items-center gap-2 text-indigo-900 mb-2"><Settings size={18} /> Usando o Construtor Nativo</h4>
                    <p className="text-sm text-gray-600">Defina logotipo, nome da clínica, dados do CRO e detalhes do rodapé em formulários. Na assinatura, você pode utilizar uma imagem com seu <strong>carimbo real + assinatura</strong>, ou enviar apenas sua <strong>assinatura</strong>, permitindo que o sistema aplique o <strong>carimbo digital</strong>.</p>
                 </div>
                 <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <h4 className="font-bold flex items-center gap-2 text-indigo-900 mb-2"><ImageIcon size={18} /> Upload de Fundo Próprio</h4>
                    <p className="text-sm text-gray-600">Possui um design exclusivo desenhado por você no Canva ou Photoshop? É só exportar a arte como arquivo local e fazer o <strong>upload em formato .jpg ou .png</strong> para que ele sirva como o plano de fundo dos seus relatórios!</p>
                 </div>
               </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">2</span>
                 Criando Modelos Inteligentes
               </h3>
               <p>
                 Na aba <strong>Templates</strong>, você pode usar modelos prontos do sistema, duplicar modelos existentes ou criar seus próprios documentos do zero.
               </p>

               <p>
                 Isso permite padronizar receitas, atestados, declarações e orientações clínicas para diferentes situações do dia a dia.
               </p>

               <p>
                 Depois, basta selecionar o profissional, o paciente e gerar o documento em PDF ou enviar por e-mail.
               </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">3</span>
                 Gerando Documentos para Contratos
               </h3>
               <p>
                 Em <strong>Contratos</strong>, a lógica é semelhante.
               </p>

               <p>
                 Escolha um template, selecione o paciente, o profissional responsável e personalize os campos específicos do contrato.
               </p>

               <p>
                 Depois de salvo, você poderá gerar um link seguro para envio e assinatura remota pelo paciente ou responsável.
               </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">4</span>
                 Geração do PDF Integrado
               </h3>
               <p>
                 Após a assinatura, clique em <strong>Atualizar Assinatura</strong> para sincronizar o documento com o sistema.
               </p>

               <p>
                 O PDF final será gerado com <strong>QR Code</strong> e <strong>link público de validação</strong>.
               </p>

               <p>
                 Isso permite confirmar a autenticidade do documento e até verificar a integridade do PDF enviado.
               </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-purple-200 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-purple-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-black">5</span>
                 Conferência Contextual com IA
               </h3>
               <p>Na aba <strong>Documentos</strong> do prontuário, abra a ação <strong>Conferir com IA</strong> e clique em <strong>Conferir documento</strong>. A ferramenta verifica campos essenciais, itens ausentes e alertas de preenchimento antes da emissão.</p>
               <div className="mt-4 bg-purple-50 p-4 rounded-xl border border-purple-200 flex items-start gap-3">
                 <Sparkles className="text-purple-600 shrink-0 mt-1" size={20} />
                 <p className="text-purple-900 text-sm mb-0">A conferência não valida indicação clínica, dose, diagnóstico ou conformidade jurídica. O conteúdo não é alterado automaticamente e exige revisão final da profissional.</p>
               </div>
            </div>
          </div>

          <div className="bg-indigo-900 rounded-3xl p-8 mt-12 text-white">
             <h3 className="text-2xl font-bold mb-4">Recomendação Legal</h3>
             <ul className="space-y-3">
               <li className="flex items-start gap-3">
                 <CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} />
                 <span>Receituários e Contratos enviados no WhatsApp têm validade e melhoram a taxa de adesão, mas <strong>receituários controlados</strong> ainda demandam a assinatura formal em conformidade com as legislações regionais. Tenha sua marca customizada perfeitamente alinhada em caso de impressões A4!</span>
               </li>
             </ul>
          </div>
        </article>
      </main>
    </div>
  );
}
